import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTheme, Headline, ActivityIndicator, RadioButton } from "react-native-paper";
import {
  View,
  StyleSheet,
  ScrollView,
  Linking,
  Pressable,
  Platform,
  Dimensions,
} from "react-native";
import { Text, Screen, Button, PaymentRejected } from "../../components";
import { Stack } from "../../components/layout/stack";
import { ThemeType } from "../../theme/theme";
import { useAuthData } from "../../hooks/useAuthData";
import { RootState } from "../../redux/reducers";
import { translate } from "../../i18n/translate";
import { DomusoApiService } from "../../api/DomusoApiService";
import { AntDesign } from "@expo/vector-icons";
import { toHumanDollars } from "../../utils/money";
import { useNavigation, useRoute, useIsFocused, useFocusEffect } from "@react-navigation/native";
import { useReservationInformation } from "../../hooks/useReservationInformation";
import { Spacer } from "../../components/spacer/spacer";
import { useConfig } from "../../hooks/useConfig";
import { testProps } from "../../utils/testProps";

const windowHeight = Dimensions.get("window").height;
const windowHeightAdjusted = windowHeight > 999 ? windowHeight - 86 - 54 : windowHeight * 0.75;

const windowWidth = Dimensions.get("window").width;
const windowWidthAdjusted = windowWidth >= 804 ? 804 : windowWidth;

export const DCashRecoveryInit = ({ route }: any) => {
  const dispatch = useDispatch();
  const [selectedPaymentForRecoveryFlow, setSelectedPaymentForRecoveryFlow] = useState({});
  const logger = useSelector((state: RootState) => state.logger);
  const { userInfo } = useAuthData(logger);
  const cognitoUser = useSelector((state) => state.auth.cognitoUser);
  const domusoApiService = new DomusoApiService(cognitoUser, dispatch);
  const theme = useTheme() as ThemeType;
  const styles = getStyles(theme);
  const navigation = useNavigation();
  const config = useConfig();
  const { params } = route;
  const { reservations, isSuperBlackList } = useReservationInformation("DCashRecoveryInit", true);

  const dcashRecoveryReservationsList = reservations || [];
  const listOfPayments = [];
  const currentPropertyAllInfoByReservationId = {};
  const allowedPaymentsByReservationId = {};
  const accountingDetailsByReservationId = {};

  const amountOwed = dcashRecoveryReservationsList.reduce((allTotal, reservation) => {
    currentPropertyAllInfoByReservationId[reservation.id] = {
      ...{
        property_id: reservation.property.id,
        property_name: reservation.property.name,
        property_code: reservation.property.code,
        property_active: reservation.property.active,
        property_company_id: reservation.property.company_id,
        propery_unit: reservation.property_unit,
      },
      ...reservation.property_profile,
      ...reservation.property_fee_profile,
    };

    allowedPaymentsByReservationId[reservation.id] = reservation.allowed_payments;
    accountingDetailsByReservationId[reservation.id] = reservation.accounting_details;

    const sum = reservation.recovery_payments.reduce((total, payment) => {
      listOfPayments.push({
        reservationId: reservation.id,
        propertyUnit: reservation.property_unit,
        blockedReservationId: payment.blocked_reservations_id,
        paymentID: payment.id,
        paymentOrderId: payment.order_id,
        paymentDate: payment.capture_date,
        paymentAccountNumber: payment.last_four_digits,
        total: payment.total,
        paymentRejectReason: payment.reject_reason,
        paymentMethod: payment.type,
        paymentMethodCardType: payment.card_type,
        paymentMethodBankType: payment.bank_account_type,
      });

      return total + Math.abs(payment.total);
    }, 0);
    return allTotal + sum;
  }, 0);

  const onActivateButtonPressed = () => {
    // TODO:
    //  currentPropertyProfile??
    //  currentPropertyFeeProfile??
    const reservationId = selectedPaymentForRecoveryFlow.reservationId;

    const params = {
      reservationId: reservationId,
      propertyUnit: selectedPaymentForRecoveryFlow.propertyUnit,
      blockedReservationId: selectedPaymentForRecoveryFlow.blockedReservationId,
      balanceDue: selectedPaymentForRecoveryFlow.total,
      currentPropertyProfile: currentPropertyAllInfoByReservationId[reservationId],
      allowedPayments: allowedPaymentsByReservationId[reservationId],
      accountingDetails: accountingDetailsByReservationId[reservationId],
      recoveryPayment: selectedPaymentForRecoveryFlow,
      hasRemainingDCashRecoveryPayments: listOfPayments.length > 1,
      remainingDCashRecoveryPaymentsAmount: amountOwed - selectedPaymentForRecoveryFlow.total,
    };

    navigation.navigate("Actions", {
      screen: "DCashRecoveryEnterPaymentDetails",
      params,
    });
  };

  let recoveryPaymentActionUI = (
    <View>
      <Text style={styles.bodyText}>{translate("dcashRecoveryInit.callToActionText")}</Text>
      <View style={styles.buttonContainer}>
        <Button
          tx="dcashRecoveryInit.callToActionButton"
          onPress={onActivateButtonPressed}
          mode="contained"
          style={styles.actionButton}
          {...testProps("dcash-recovery-init@button-start-payment-flow")}
        />
      </View>
    </View>
  );

  /* Conditions for changing the recovery payment action UI */
  if (isSuperBlackList) {
    recoveryPaymentActionUI = (
      <View>
        <Text style={styles.bodyText}>
          {translate("dcashRecoveryInit.callToActionTextSuperBlackList")}
        </Text>
        <View style={styles.buttonContainer}>
          <Button
            text={`${translate("dcashRecoveryInit.callToActionButtonOverSuperBlackListCall")} ${
              config.phoneSupport
            }`}
            onPress={() => {
              Linking.openURL(`tel:${config.phoneSupport}`);
            }}
            mode="contained"
            {...testProps("dcash-recovery-init@button-super-block-call-cs")}
          />
        </View>
        <Spacer size="small" />
        <View style={styles.buttonContainer}>
          <Button
            text={`${translate("dcashRecoveryInit.callToActionButtonOverSuperBlackListEmail")} ${
              config.emailSupport
            }`}
            onPress={() => {
              Linking.openURL(`mailto: ${config.emailSupport}`);
            }}
            mode="contained"
            style={styles.actionButton}
            {...testProps("dcash-recovery-init@button-super-block-email-cs")}
          />
        </View>
      </View>
    );
  }

  useEffect(() => {
    if (listOfPayments.length >= 1) {
      setSelectedPaymentForRecoveryFlow(listOfPayments[0]);
    }
  }, [reservations]);

  return (
    <Screen
      style={styles.container}
      unsafe={true}
      preset="scroll"
      backgroundColor={theme.colors.background}>
      <Stack space="large">
        <ScrollView scrollEnabled={true} style={styles.containerScroll}>
          <Headline style={styles.header}>
            <AntDesign name="exclamationcircle" style={styles.headerIcon} />
            {` ${translate("dcashRecoveryInit.header")}`}
          </Headline>
          <Headline style={styles.subHeader}>{translate("dcashRecoveryInit.subHeader")}</Headline>

          {(() => {
            if (listOfPayments.length == 0) {
              return (
                <View style={styles.bodyText}>
                  <ActivityIndicator />
                  <Text style={styles.bodyText}>{translate("dcashRecoveryInit.loadingMsg")}</Text>
                </View>
              );
            } else {
              return (
                <View>
                  <View>
                    {listOfPayments.map((recoveryPayment, index) => {
                      return (
                        <Pressable
                          style={styles.scrollViewRow}
                          onPress={() => {
                            setSelectedPaymentForRecoveryFlow(recoveryPayment);
                          }}>
                          <View style={styles.row}>
                            <View style={{ height: 50 }}>
                              <RadioButton
                                color={theme.colors.darkBg}
                                status={
                                  selectedPaymentForRecoveryFlow.paymentID ==
                                  recoveryPayment.paymentID
                                    ? "checked"
                                    : "unchecked"
                                }
                                onPress={() => {
                                  setSelectedPaymentForRecoveryFlow(recoveryPayment);
                                }}
                                {...testProps(
                                  `dcash-recovery-init@payment-row-${recoveryPayment.paymentID}`
                                )}
                              />
                            </View>
                            <View>
                              <PaymentRejected
                                index={index}
                                paymentID={recoveryPayment.paymentID}
                                paymentOrderId={recoveryPayment.paymentOrderId}
                                paymentDate={recoveryPayment.paymentDate}
                                paymentAccountNumber={recoveryPayment.paymentAccountNumber}
                                paymentAmount={recoveryPayment.total}
                                paymentRejectReason={recoveryPayment.paymentRejectReason}
                                paymentMethod={recoveryPayment.paymentMethod}
                                paymentMethodCardType={recoveryPayment.paymentMethodCardType}
                                paymentMethodBankType={recoveryPayment.paymentMethodBankType}
                              />
                            </View>
                          </View>
                        </Pressable>
                      );
                    })}
                  </View>
                  <Text style={styles.bodyText}>
                    {translate("dcashRecoveryInit.amountOwed")?.replace(
                      "<amountOwed>",
                      toHumanDollars(amountOwed)
                    )}
                  </Text>
                  {recoveryPaymentActionUI}
                </View>
              );
            }
          })()}
        </ScrollView>
      </Stack>
    </Screen>
  );
};

const getStyles = (theme: ThemeType) => {
  const text = { color: theme.colors.surface };
  const { colors, spacing } = theme;
  return StyleSheet.create({
    container: {
      flex: 1,
      alignItems: "center",
      ...Platform.select({
        native: {
          backgroundColor: colors.background,
        },
        default: {
          backgroundColor: colors.lightGrey,
        },
      }),
    },
    containerScroll: {
      width: windowWidthAdjusted,
      height: windowHeightAdjusted,
      ...Platform.select({
        native: {
          padding: 0,
        },
        default: {
          padding: 25,
        },
      }),
    },
    header: {
      marginTop: 14,
      fontSize: 30,
      textAlign: "center",
      fontWeight: "600",
      display: "flex",
      justifyContent: "center",
    },
    headerIcon: {
      fontSize: 20,
      color: "#D81620",
    },
    subHeader: {
      textAlign: "center",
      fontSize: 16,
      fontWeight: "700",
      lineHeight: 21,
      marginTop: 10,
      marginBottom: 10,
      ...Platform.select({
        native: {
          marginLeft: 15,
          marginRight: 15,
        },
      }),
    },
    row: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      borderBottomColor: colors.lightGrey,
      borderBottomWidth: 1,
      marginBottom: 12,
    },
    bodyText: {
      textAlign: "center",
      fontSize: 16,
      fontWeight: "400",
      marginBottom: 20,
      marginTop: 20,
      ...Platform.select({
        native: {
          marginLeft: 15,
          marginRight: 15,
        },
      }),
    },
    buttonContainer: {
      ...Platform.select({
        default: {
          width: 325,
          marginLeft: "auto",
          marginRight: "auto",
        },
      }),
    },
    actionButton: {
      marginBottom: 125,
    },
  });
};
